import { auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input } from "@lumberset/ui";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { isUnconfirmedEmail, UnconfirmedEmail } from "./-unconfirmed-email";

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: Login,
});

function Login() {
  const router = useRouter();
  const search = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  /** The address to confirm, once a right password met an unconfirmed email. */
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email"));
    setBusy(true);
    setError(null);
    setUnconfirmed(null);

    const result = await auth.signIn.email({
      email,
      password: String(form.get("password")),
    });

    if (result.error) {
      setBusy(false);
      if (isUnconfirmedEmail(result.error)) setUnconfirmed(email);
      else setError(result.error.message ?? "That email and password do not match.");
      return;
    }
    // Only follow a redirect back into this app, never to another site.
    const target = search.redirect?.startsWith("/") ? search.redirect : "/";
    await router.invalidate();
    router.history.push(target);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <Heading>Sign in</Heading>
        <p className="txt-small mt-1 text-ui-fg-subtle">Welcome back to LumberSet.</p>
      </div>

      <Field label="Email">
        {(control) => (
          <Input
            {...control}
            name="email"
            type="email"
            autoComplete="email"
            required
            size="large"
            autoFocus
          />
        )}
      </Field>

      <Field label="Password">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            size="large"
          />
        )}
      </Field>

      {unconfirmed ? <UnconfirmedEmail email={unconfirmed} /> : null}
      {error ? <Hint variant="error">{error}</Hint> : null}

      <Button type="submit" size="large" block isLoading={busy}>
        Sign in
      </Button>

      <div className="txt-small flex items-center justify-between text-ui-fg-subtle">
        <Link
          to="/forgot-password"
          className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
        >
          Forgot password?
        </Link>
        <span>
          New here?{" "}
          {/* Someone opening an invitation comes back to it once their account exists. */}
          <Link
            to="/signup"
            search={
              search.redirect?.startsWith("/accept-invitation/")
                ? { redirect: search.redirect }
                : {}
            }
            className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
          >
            Create an account
          </Link>
        </span>
      </div>
    </form>
  );
}
